import { createRoot } from "react-dom/client"
import type { WebViewerInstance } from "@pdftron/webviewer"
import { StyleProvider } from "@ant-design/cssinjs"
import { ElevationReferencesToggle } from "../ElevationReferencesSidebar/ElevationReferencesToggle/ElevationReferencesToggle"
import { BOTTOM_DOCK_ATTRIBUTE } from "helpers/annotationFraming.helper"
import type { RootEntry } from "./shadowPortal"
import marqueeStyles from "./connectMarquee.module.scss"
import draftRectStyles from "./erefDraftRect.module.scss"

// Overhang in px that extends a full-width top bar past the document area on each
// side so it visually covers the panel-resizer gutters next to the sidebars.
const TOP_BAR_SIDE_OVERHANG = 13

// The overhang to use for a top bar, given whether split screen is open (i.e. whether a live split
// pane exists). In single-pane mode it covers the resizer gutters; in split mode a bar only spans
// ONE half-width pane, so the same overhang spills over the pane divider on one side and a docked
// sidebar on the other. Exported so the ResizeObserver-driven `reposition` below and
// useJumpNavigation's render effect resolve it identically — when only one of them knew about the
// split state, any resize (or opening the middle dock) put the overhang straight back.
export const resolveTopBarSideOverhang = (isSplitPaneLive: boolean): number =>
	isSplitPaneLive ? 0 : TOP_BAR_SIDE_OVERHANG

// Position a full-width bar across the top of the document area (the jumping-nav bar, the
// connect-objects bar). `offsetTop` stacks one bar below another: pass the height of whatever
// already occupies the slot, so the two never cover each other.
export const positionTopBarOverlay = (
	wrapper: HTMLElement | null | undefined,
	docEl: HTMLElement | null | undefined,
	targetEl: HTMLElement | null | undefined,
	offsetTop = 0,
	// See resolveTopBarSideOverhang: 0 for a bar confined to one pane of the split screen.
	sideOverhang = TOP_BAR_SIDE_OVERHANG,
) => {
	if (!wrapper || !docEl || !targetEl) return
	const docRect = docEl.getBoundingClientRect()
	const targetRect = targetEl.getBoundingClientRect()
	wrapper.style.left = `${docRect.left - targetRect.left - sideOverhang}px`
	wrapper.style.top = `${docRect.top - targetRect.top + offsetTop}px`
	wrapper.style.width = `${docRect.width + sideOverhang * 2}px`
}

// Distance from the top of the VIEWER (not of the document area) to the top of the toggle wrapper.
// The button itself is offset a further 12px inside the wrapper by its own stylesheet, so it lands
// just below the ~73px header — which is where it sat before split screen existed.
//
// Measuring from the viewer and not from the pane is deliberate. The pre-split code set a literal
// `top: 70px` on a wrapper whose containing block turned out to be the full-height viewer box at
// y=0 (`.document-content-container` is `position: static`, so `absolute` skipped past it). Adding
// 70 to the pane's top instead pushed the button a further header-height down the page (y=155
// rather than y=82) — visibly adrift in the middle of the drawing.
const TOGGLE_TOP_OFFSET = 70

// The toggle wrapper spans the pane's document area horizontally so its button can anchor to either
// the wrapper's top (default) or bottom (when the jumping nav bar is up). Its LEFT edge tracks the
// LIVE pane rect — that is what keeps the button over the left screen in split mode, where Apryse
// re-mounts the document area. Its TOP is fixed to the viewer, per the constant above, plus
// `extraTopOffset` (REQ-400's shape tool-options row or REQ-395/396's note tool-options row, when
// either is showing as a second header row above the document area — that row isn't part of the
// viewer height this constant was measured against, so it has to be added on top rather than folded
// into the constant itself).
// REQ-399 / #1264.
const positionToggleOverlay = (
	wrapper: HTMLElement | null | undefined,
	docEl: HTMLElement | null | undefined,
	targetEl: HTMLElement | null | undefined,
	extraTopOffset = 0,
) => {
	if (!wrapper || !docEl || !targetEl) return
	const docRect = docEl.getBoundingClientRect()
	const targetRect = targetEl.getBoundingClientRect()
	const top = TOGGLE_TOP_OFFSET + extraTopOffset
	wrapper.style.left = `${docRect.left - targetRect.left}px`
	wrapper.style.top = `${top}px`
	wrapper.style.width = `${docRect.width}px`
	wrapper.style.height = `${Math.max(0, targetRect.height - top)}px`
}

const PAGE_NAV_BOTTOM_OFFSET = 24

// Gap between the keyboard-shortcuts panel and the viewer's left / right / bottom edges (REQ-359).
export const SHORTCUTS_PANEL_INSET = 9

// Internal to this module — only the reposition closure below uses it. (The jumping-nav variant
// is exported because the orchestrator's jumping-nav render effect also positions it.)
const positionPageNavOverlay = (
	wrapper: HTMLElement | null | undefined,
	docEl: HTMLElement | null | undefined,
	targetEl: HTMLElement | null | undefined,
) => {
	if (!wrapper || !docEl || !targetEl) return
	const docRect = docEl.getBoundingClientRect()
	const targetRect = targetEl.getBoundingClientRect()
	const centerX = docRect.left + docRect.width / 2 - targetRect.left
	const bottomY = docRect.bottom - targetRect.top - PAGE_NAV_BOTTOM_OFFSET
	wrapper.style.left = `${centerX}px`
	wrapper.style.top = `${bottomY}px`
}

type TOverlayContainers = {
	toggle: RootEntry
	jumpingNav: RootEntry
	// Second jumping-nav bar, shown over the split-screen pane (#1311). Smart navigation puts the
	// bar in the SECONDARY pane, which is the split pane whenever the user's last selection was made
	// in the primary one. Created up front and left hidden, exactly like `splitPageNav` below.
	splitJumpingNav: RootEntry
	pageNav: RootEntry
	// Second page-nav bar, shown over the split-screen pane (REQ-399). Created up front and left
	// hidden; the split pane's own usePageNavigation instance renders into it.
	splitPageNav: RootEntry
	contextMenu: RootEntry
	selectionToolbar: RootEntry
	// Floating "delete highlight" toolbar (REQ-400) — see its container's own comment below.
	highlightToolbar: RootEntry
	// Floating "delete note" toolbar (REQ-395/396) — see its container's own comment below.
	noteToolbar: RootEntry
	connectObjects: RootEntry
	// Keyboard-shortcuts help panel (REQ-359), docked across the bottom of the viewer.
	shortcutsPanel: RootEntry
	connectMarquee: HTMLElement
	// The draft-area box of the "add a detection" modes (REQ-292 / REQ-293) and its four corner
	// handles, in top-left / top-right / bottom-left / bottom-right order.
	erefDraftRect: { element: HTMLElement; handles: HTMLElement[] }
	appContainer: HTMLElement
	docAreaRefEl: HTMLElement
	resizeObserver: ResizeObserver
	reposition: () => void
	// Re-point the ResizeObserver at the CURRENT pane scroll elements. Entering/exiting
	// MultiViewerMode re-mounts the document area, detaching whatever was observed before — an
	// observer on a detached node never fires again, so the overlays would stop tracking resizes.
	rebindObservers: () => void
	getPaneDocAreaEl: (pane: "primary" | "split") => HTMLElement | null
}

// Create the floating shadow-DOM overlay containers for the quotation viewer: the elevation-
// references toggle button, the jumping-nav bar, the page-nav pills, the right-click context
// menu, and the batch selection toolbar. Each is a positioned <div> appended into the Apryse
// shadow DOM with its own React root (mounted empty; the orchestrator's render effects fill them
// in). Also builds the shared reposition function + ResizeObserver that keep the jumping-nav and
// page-nav overlays tracking sidebar open/close, modular-header changes, and window resize.
//
// Returns the created roots/observer/reposition (or null if no host container is found); the
// caller owns the refs and their unmount teardown.
export const createOverlayContainers = (
	shadowRoot: ShadowRoot,
	instance: WebViewerInstance,
	setIsPanelOpen: (open: boolean) => void,
	// How far below the top of the document area the connect-objects bar must sit, so it stacks under
	// the jumping-nav bar instead of covering it. A getter (not a value): the containers are built
	// once, while the bars come and go, so every reposition asks again.
	getConnectBarOffset: () => number = () => 0,
	// REQ-400 / REQ-395/396: extra px to push the elevation-references toggle down by, on top of
	// TOGGLE_TOP_OFFSET, while EITHER tool-options row (shape or note) is showing as a second header
	// row (see .wv-header-stack in webviewer-quotation.css). Same getter-not-value shape as
	// getConnectBarOffset, for the same reason — the row comes and goes independently of whatever
	// triggered this reposition pass.
	getToggleExtraTopOffset: () => number = () => 0,
): TOverlayContainers | null => {
	const { UI } = instance
	const docContainer =
		shadowRoot.querySelector(".document-content-container") ||
		shadowRoot.querySelector(".DocumentContainer") ||
		shadowRoot.querySelector("[data-element='documentContainer']")
	const targetContainer = docContainer || shadowRoot.querySelector(".App")

	if (!targetContainer) return null

	// `.App` is the coordinate origin for every overlay: it is the one host that survives entering
	// and leaving split screen. The document area is NOT — Apryse unmounts
	// `.document-content-container` entirely in MultiViewerMode (`App.js`:
	// `{!isMultiViewerMode && <DocumentContainer />}`), which is what used to take the elevation-
	// references toggle down with it and never bring it back. REQ-399 / #1264.
	const appContainer = (shadowRoot.querySelector(".App") as HTMLElement | null) || (targetContainer as HTMLElement)
	const scrollViewEl = instance.Core.documentViewer.getScrollViewElement() as HTMLElement | null
	const docAreaRefEl: HTMLElement =
		scrollViewEl ?? ((shadowRoot.querySelector(".document-content-container") as HTMLElement | null) || appContainer)

	const toggleContainer = document.createElement("div")
	// Wrapper spans the primary pane's doc area so the inner button can anchor to either
	// top (default) or bottom (when the jumping nav bar is shown). Wrapper
	// is non-interactive; the button itself receives clicks. Its box is set by
	// positionToggleOverlay on every reposition pass.
	toggleContainer.style.position = "absolute"
	toggleContainer.style.zIndex = "1000"
	toggleContainer.style.pointerEvents = "none"
	toggleContainer.style.display = "none"

	appContainer.appendChild(toggleContainer)

	const toggle: RootEntry = { root: createRoot(toggleContainer), container: toggleContainer }
	toggle.root.render(
		<StyleProvider container={shadowRoot}>
			<ElevationReferencesToggle
				onOpen={() => {
					UI.openElements(["elevation-references-panel"])
					setIsPanelOpen(true)
				}}
			/>
		</StyleProvider>,
	)

	// Jumping navigation bar overlay — must sit BETWEEN the left and right sidebars
	// and BELOW the WebViewer modular header. Both addPanel sidebars and the
	// modular header are overlays inside `.App`, so we can't rely on CSS
	// left:0/right:0 of any single container. Instead we:
	//   parent (wrapper attached to)  = `.App` (full viewport, our coord origin)
	//   reference (doc viewing area)  = documentViewer.getScrollViewElement(),
	//                                   whose getBoundingClientRect() respects
	//                                   the current sidebar + header layout.
	// We recompute on ResizeObserver so the bar tracks sidebar open/close,
	// modular-header changes, and window resize.
	const jumpingNavContainer = document.createElement("div")
	jumpingNavContainer.style.position = "absolute"
	jumpingNavContainer.style.zIndex = "900"
	jumpingNavContainer.style.pointerEvents = "auto"
	jumpingNavContainer.style.display = "none"

	appContainer.appendChild(jumpingNavContainer)

	const jumpingNav: RootEntry = { root: createRoot(jumpingNavContainer), container: jumpingNavContainer }
	jumpingNav.root.render(null)

	// The split pane's jumping-nav bar (#1311) — same container treatment, positioned against the
	// SECOND viewer's document area so the bar spans that pane only (the design's 358px width).
	const splitJumpingNavContainer = document.createElement("div")
	splitJumpingNavContainer.style.position = "absolute"
	splitJumpingNavContainer.style.zIndex = "900"
	splitJumpingNavContainer.style.pointerEvents = "auto"
	splitJumpingNavContainer.style.display = "none"

	appContainer.appendChild(splitJumpingNavContainer)

	const splitJumpingNav: RootEntry = {
		root: createRoot(splitJumpingNavContainer),
		container: splitJumpingNavContainer,
	}
	splitJumpingNav.root.render(null)

	// Shares the jumping-nav parent + doc reference so they reposition together.
	const pageNavContainer = document.createElement("div")
	pageNavContainer.style.position = "absolute"
	pageNavContainer.style.zIndex = "1200"
	pageNavContainer.style.pointerEvents = "auto"
	pageNavContainer.style.transform = "translate(-50%, -100%)"
	pageNavContainer.style.transition = "opacity 0.25s ease"
	pageNavContainer.style.opacity = "0"

	appContainer.appendChild(pageNavContainer)

	const pageNav: RootEntry = { root: createRoot(pageNavContainer), container: pageNavContainer }
	pageNav.root.render(null)

	// Split-screen pane's page-nav bar (REQ-399) — identical container, positioned against the
	// SECOND viewer's document area. Stays at opacity 0 until that pane exists and reports scroll.
	const splitPageNavContainer = document.createElement("div")
	splitPageNavContainer.style.position = "absolute"
	splitPageNavContainer.style.zIndex = "1200"
	splitPageNavContainer.style.pointerEvents = "auto"
	splitPageNavContainer.style.transform = "translate(-50%, -100%)"
	splitPageNavContainer.style.transition = "opacity 0.25s ease"
	splitPageNavContainer.style.opacity = "0"

	appContainer.appendChild(splitPageNavContainer)

	const splitPageNav: RootEntry = { root: createRoot(splitPageNavContainer), container: splitPageNavContainer }
	splitPageNav.root.render(null)

	// Right-click context-menu overlay (Copy / Paste). Positioned at the cursor
	// in app-container-relative px by the render effect; above the page-nav z-index.
	const contextMenuContainer = document.createElement("div")
	contextMenuContainer.style.position = "absolute"
	// Above the keyboard-shortcuts panel's 1300: a right-click menu opened near the bottom of the
	// drawing extends downwards, and at 1100 it was drawn BEHIND that panel — the menu opened and was
	// invisible. It is the most transient thing on screen, so it wins over every docked overlay.
	contextMenuContainer.style.zIndex = "1400"
	contextMenuContainer.style.pointerEvents = "auto"
	contextMenuContainer.style.display = "none"

	appContainer.appendChild(contextMenuContainer)

	const contextMenu: RootEntry = { root: createRoot(contextMenuContainer), container: contextMenuContainer }
	contextMenu.root.render(null)

	// Floating batch toolbar overlay (REQ-369). Its clipping container matches the
	// visible document area and ends before the PDF scrollbar.
	const selectionToolbarOverlay = document.createElement("div")
	selectionToolbarOverlay.style.position = "absolute"
	selectionToolbarOverlay.style.zIndex = "800"
	selectionToolbarOverlay.style.overflow = "hidden"
	selectionToolbarOverlay.style.pointerEvents = "none"
	appContainer.appendChild(selectionToolbarOverlay)

	// The inner container is centered on and lifted above the selection's top edge
	// via a CSS transform. The render effect positions it inside the clipping area.
	const selectionToolbarContainer = document.createElement("div")
	selectionToolbarContainer.style.position = "absolute"
	selectionToolbarContainer.style.pointerEvents = "auto"
	selectionToolbarContainer.style.transform = "translate(-50%, calc(-100% - 8px))"
	selectionToolbarContainer.style.display = "none"

	selectionToolbarOverlay.appendChild(selectionToolbarContainer)

	const selectionToolbar: RootEntry = {
		root: createRoot(selectionToolbarContainer),
		container: selectionToolbarContainer,
	}
	selectionToolbar.root.render(null)

	// Floating "delete note" toolbar (REQ-395/396) — same clipping + centered-above shape as the
	// batch selection pill above, kept in its own overlay rather than folded into that one: a note
	// selection is a wholly separate concept from the furniture batch pill (a note is a REAL Apryse
	// annotation the user selects natively, never tracked in metadataMap), so reusing the SAME
	// container risked entangling the two pills' very different lifecycle rules. Mirrors PR #1381's
	// `highlightToolbar` overlay.
	const noteToolbarOverlay = document.createElement("div")
	noteToolbarOverlay.style.position = "absolute"
	noteToolbarOverlay.style.zIndex = "800"
	noteToolbarOverlay.style.overflow = "hidden"
	noteToolbarOverlay.style.pointerEvents = "none"
	appContainer.appendChild(noteToolbarOverlay)

	const noteToolbarContainer = document.createElement("div")
	noteToolbarContainer.style.position = "absolute"
	noteToolbarContainer.style.pointerEvents = "auto"
	noteToolbarContainer.style.transform = "translate(-50%, calc(-100% - 8px))"
	noteToolbarContainer.style.display = "none"
	noteToolbarOverlay.appendChild(noteToolbarContainer)

	const noteToolbar: RootEntry = {
		root: createRoot(noteToolbarContainer),
		container: noteToolbarContainer,
	}
	noteToolbar.root.render(null)

	// Floating "delete highlight" toolbar (REQ-400 decision 2/3) — same clipping + centered-above
	// shape as the batch selection pill above, kept in its own overlay rather than folded into that
	// one: a highlight pick is a wholly separate selection concept (highlights are excluded from
	// metadataMap / getSelectionScreenBounds by design — see AnnotationService's Highlights section),
	// so reusing the SAME container risked entangling the two pills' very different lifecycle rules.
	const highlightToolbarOverlay = document.createElement("div")
	highlightToolbarOverlay.style.position = "absolute"
	highlightToolbarOverlay.style.zIndex = "800"
	highlightToolbarOverlay.style.overflow = "hidden"
	highlightToolbarOverlay.style.pointerEvents = "none"
	appContainer.appendChild(highlightToolbarOverlay)

	const highlightToolbarContainer = document.createElement("div")
	highlightToolbarContainer.style.position = "absolute"
	highlightToolbarContainer.style.pointerEvents = "auto"
	highlightToolbarContainer.style.transform = "translate(-50%, calc(-100% - 8px))"
	highlightToolbarContainer.style.display = "none"
	highlightToolbarOverlay.appendChild(highlightToolbarContainer)

	const highlightToolbar: RootEntry = {
		root: createRoot(highlightToolbarContainer),
		container: highlightToolbarContainer,
	}
	highlightToolbar.root.render(null)

	// "Connect objects" bar overlay (REQ-372). Shares the jumping-nav bar's slot — pinned across the
	// top of the document area — and stacks BELOW it when a jump session is up (see
	// getConnectBarOffset), so starting to pick objects never hides the navigation. Above the jumping
	// nav's z-index so it also clears the elevation-references toggle.
	const connectObjectsContainer = document.createElement("div")
	connectObjectsContainer.style.position = "absolute"
	connectObjectsContainer.style.zIndex = "1000"
	connectObjectsContainer.style.pointerEvents = "auto"
	connectObjectsContainer.style.display = "none"

	appContainer.appendChild(connectObjectsContainer)

	const connectObjects: RootEntry = { root: createRoot(connectObjectsContainer), container: connectObjectsContainer }
	connectObjects.root.render(null)

	// Keyboard-shortcuts help panel (REQ-359). The only overlay that needs no reposition pass: it is
	// pinned to `.App`'s own edges rather than to the document area, which is what the design shows
	// (the panel runs under both sidebars) and what keeps it in place across split-screen enter/exit
	// — the one host Apryse never unmounts. Above the page-nav pills, which it covers.
	const shortcutsPanelContainer = document.createElement("div")
	shortcutsPanelContainer.style.position = "absolute"
	shortcutsPanelContainer.style.left = `${SHORTCUTS_PANEL_INSET}px`
	shortcutsPanelContainer.style.right = `${SHORTCUTS_PANEL_INSET}px`
	shortcutsPanelContainer.style.bottom = `${SHORTCUTS_PANEL_INSET}px`
	shortcutsPanelContainer.style.zIndex = "1300"
	// none, not auto: the host is a bare box and the panel inside it has 16px rounded corners, so an
	// `auto` host swallows the clicks that land in the four corner slivers outside the panel. The panel
	// itself takes pointer events back (see KeyboardShortcutsPanel.module.scss).
	shortcutsPanelContainer.style.pointerEvents = "none"
	shortcutsPanelContainer.style.display = "none"
	// Lets navigation framing subtract the panel from the visible document area (REQ-455).
	shortcutsPanelContainer.setAttribute(BOTTOM_DOCK_ATTRIBUTE, "")

	appContainer.appendChild(shortcutsPanelContainer)

	const shortcutsPanel: RootEntry = { root: createRoot(shortcutsPanelContainer), container: shortcutsPanelContainer }
	shortcutsPanel.root.render(null)

	// The drag-to-select rectangle. A plain element (no React root): its geometry changes on every
	// mousemove, so useErefConnectObjects writes left/top/width/height straight onto it instead of
	// re-rendering the viewer tree. Sits under the bars and never takes pointer events.
	const connectMarquee = document.createElement("div")
	connectMarquee.className = marqueeStyles.marquee
	connectMarquee.style.zIndex = "850"

	appContainer.appendChild(connectMarquee)

	// The draft-area box SHARED by every "add a detection" mode (REQ-292 elevation reference, REQ-293
	// object): the modes are mutually exclusive, so one box serves them all. Also a plain element for
	// the same reason as the marquee above, but unlike it this one SURVIVES the gesture: it stays on
	// screen as the draft area while the user fills in the sidebar, and its handles are draggable so
	// the box can be adjusted before Create. Sits above the marquee's z-index (the two never actually
	// overlap) and below the top bars.
	const erefDraftRectEl = document.createElement("div")
	erefDraftRectEl.className = draftRectStyles.draftRect
	erefDraftRectEl.style.zIndex = "860"

	const draftHandles = (
		[
			draftRectStyles.handleTopLeft,
			draftRectStyles.handleTopRight,
			draftRectStyles.handleBottomLeft,
			draftRectStyles.handleBottomRight,
		] as const
	).map((cornerClass) => {
		const handle = document.createElement("div")
		handle.className = `${draftRectStyles.handle} ${cornerClass}`
		erefDraftRectEl.appendChild(handle)
		return handle
	})

	appContainer.appendChild(erefDraftRectEl)

	// The overlay containers and the app-coordinate origin are created once here and live for the
	// instance's lifetime, so the reposition closure captures them directly. The DOC AREA is looked
	// up LIVE on every call instead: entering/exiting split screen re-mounts the document area, so
	// a captured element would go stale (and a stale rect is what leaves the page-nav pill parked
	// off-screen). REQ-399.
	const getPaneDocAreaEl = (pane: "primary" | "split"): HTMLElement | null => {
		const { Core } = instance
		// Guarded: this runs during init, and anything thrown here aborts createOverlayContainers —
		// which would take EVERY overlay down with it, not just the split pane's. `getDocumentViewers`
		// is missing on partially-stubbed Core objects (unit-test mocks).
		const splitViewer = typeof Core.getDocumentViewers === "function" ? Core.getDocumentViewers()[1] : undefined
		const viewer = pane === "split" ? splitViewer : Core.documentViewer
		const scrollEl = (viewer?.getScrollViewElement() as HTMLElement | null) ?? null
		if (scrollEl?.isConnected) return scrollEl
		if (pane === "split") return null
		return (shadowRoot.querySelector(".document-content-container") as HTMLElement | null) ?? appContainer
	}

	const reposition = () => {
		const primaryDocEl = getPaneDocAreaEl("primary")
		if (!primaryDocEl) return
		const splitDocEl = getPaneDocAreaEl("split")
		// One rule for both bars and both code paths — see resolveTopBarSideOverhang.
		const sideOverhang = resolveTopBarSideOverhang(splitDocEl !== null)
		const documentAreaEl = docContainer?.isConnected ? docContainer : primaryDocEl
		positionToggleOverlay(toggleContainer, primaryDocEl, appContainer, getToggleExtraTopOffset())
		positionTopBarOverlay(jumpingNavContainer, primaryDocEl, appContainer, 0, sideOverhang)
		positionTopBarOverlay(splitJumpingNavContainer, splitDocEl, appContainer, 0, 0)
		positionTopBarOverlay(connectObjectsContainer, primaryDocEl, appContainer, getConnectBarOffset(), sideOverhang)
		positionPageNavOverlay(pageNavContainer, primaryDocEl, appContainer)
		positionPageNavOverlay(splitPageNavContainer, getPaneDocAreaEl("split"), appContainer)
		const documentAreaRect = documentAreaEl.getBoundingClientRect()
		const scrollViewRect = primaryDocEl.getBoundingClientRect()
		const appRect = appContainer.getBoundingClientRect()
		// clientWidth ends before the native document scrollbar begins.
		const documentScrollbarLeft = scrollViewRect.left + primaryDocEl.clientWidth
		selectionToolbarOverlay.style.left = `${documentAreaRect.left - appRect.left}px`
		selectionToolbarOverlay.style.top = `${documentAreaRect.top - appRect.top}px`
		selectionToolbarOverlay.style.width = `${documentScrollbarLeft - documentAreaRect.left}px`
		selectionToolbarOverlay.style.height = `${documentAreaRect.height}px`
		// Same clipping box as the selection toolbar above — both float over the same visible
		// document area.
		highlightToolbarOverlay.style.left = selectionToolbarOverlay.style.left
		highlightToolbarOverlay.style.top = selectionToolbarOverlay.style.top
		highlightToolbarOverlay.style.width = selectionToolbarOverlay.style.width
		highlightToolbarOverlay.style.height = selectionToolbarOverlay.style.height
		noteToolbarOverlay.style.left = selectionToolbarOverlay.style.left
		noteToolbarOverlay.style.top = selectionToolbarOverlay.style.top
		noteToolbarOverlay.style.width = selectionToolbarOverlay.style.width
		noteToolbarOverlay.style.height = selectionToolbarOverlay.style.height
	}
	reposition()

	const resizeObserver = new ResizeObserver(reposition)
	const rebindObservers = () => {
		resizeObserver.disconnect()
		resizeObserver.observe(appContainer)
		const primaryDocEl = getPaneDocAreaEl("primary")
		if (primaryDocEl) resizeObserver.observe(primaryDocEl)
		const splitDocEl = getPaneDocAreaEl("split")
		if (splitDocEl) resizeObserver.observe(splitDocEl)
		reposition()
	}
	rebindObservers()

	return {
		toggle,
		jumpingNav,
		splitJumpingNav,
		pageNav,
		splitPageNav,
		contextMenu,
		selectionToolbar,
		highlightToolbar,
		noteToolbar,
		connectObjects,
		shortcutsPanel,
		connectMarquee,
		erefDraftRect: { element: erefDraftRectEl, handles: draftHandles },
		appContainer,
		docAreaRefEl,
		resizeObserver,
		reposition,
		rebindObservers,
		getPaneDocAreaEl,
	}
}
